/**
 * @birhaus/test-utils
 * 
 * Comprehensive testing and validation framework for BIRHAUS design principles.
 * 
 * Features:
 * - Cognitive Load Testing (Miller's Law validation)
 * - Accessibility Testing (WCAG AA+ compliance)
 * - Spanish Coverage Testing (Bilingual completeness)
 * - Undo Pattern Testing (Anti-confirmation validation)
 * - Visual Regression Testing (Screenshot comparison)
 * - Performance Testing (Bundle size and runtime monitoring)
 */

// Cognitive Load Testing
export {
  MillersLawValidator,
  expectNavigationCompliance,
  expectFormCompliance,
  expectTabCompliance,
  expectMillersLawCompliance
} from './cognitive'

export type {
  MillersLawViolation,
  MillersLawTestOptions
} from './cognitive'

// Accessibility Testing
export {
  AccessibilityValidator,
  expectAccessibilityCompliance,
  expectSpanishAccessibility,
  expectKeyboardAccessible
} from './accessibility'

export type {
  AccessibilityViolation,
  AccessibilityTestOptions
} from './accessibility'

// Spanish Coverage Testing
export {
  SpanishCoverageValidator,
  expectSpanishFirst,
  expectBilingualCompleteness,
  expectFinancialSpanish
} from './spanish'

export type {
  SpanishCoverageViolation,
  SpanishTestOptions
} from './spanish'

// Undo Pattern Testing
export {
  UndoPatternValidator,
  expectNoConfirmationDialogs,
  expectUndoForDestructiveActions,
  expectUndoWorks,
  expectReasonableUndoTimeout
} from './undo'

export type {
  UndoPatternViolation,
  UndoTestOptions,
  UndoTestResult
} from './undo'

// Visual Regression Testing (placeholder)
export {
  VisualRegressionValidator,
  expectVisualMatch
} from './visual'

export type {
  VisualTestOptions
} from './visual'

// Performance Testing (placeholder)
export {
  PerformanceValidator,
  expectPerformanceBudget
} from './performance'

export type {
  PerformanceMetrics,
  PerformanceTestOptions
} from './performance'

// Lighthouse Integration
export {
  BirhausLighthouseValidator,
  runBirhausLighthouseAudit,
  expectLighthouseScore
} from './lighthouse'

export type {
  BirhausLighthouseConfig,
  BirhausLighthouseResult
} from './lighthouse'

// Import types for interfaces (avoiding circular dependencies)
import type { 
  MillersLawTestOptions, 
  MillersLawViolation 
} from './cognitive'
import type { 
  AccessibilityTestOptions, 
  AccessibilityViolation 
} from './accessibility'
import type { 
  SpanishTestOptions, 
  SpanishCoverageViolation 
} from './spanish'
import type { 
  UndoTestOptions, 
  UndoPatternViolation 
} from './undo'
import type { VisualTestOptions } from './visual'
import type { PerformanceTestOptions } from './performance'

// Import classes for function implementations
import { MillersLawValidator } from './cognitive'
import { AccessibilityValidator } from './accessibility'
import { SpanishCoverageValidator } from './spanish'
import { UndoPatternValidator } from './undo'

// Comprehensive BIRHAUS test suite
export interface BirhausTestOptions {
  cognitive?: MillersLawTestOptions
  accessibility?: AccessibilityTestOptions
  spanish?: SpanishTestOptions
  undo?: UndoTestOptions
  visual?: VisualTestOptions
  performance?: PerformanceTestOptions
}

export interface BirhausTestResult {
  cognitiveLoadScore: number
  accessibilityScore: number
  spanishCoverageScore: number
  undoPatternScore: number
  overallScore: number
  violations: Array<
    MillersLawViolation | 
    AccessibilityViolation | 
    SpanishCoverageViolation | 
    UndoPatternViolation
  >
  passed: boolean
}

/**
 * Run comprehensive BIRHAUS compliance test suite
 */
export async function runBirhausTestSuite(
  container: HTMLElement = document.body,
  options: BirhausTestOptions = {}
): Promise<BirhausTestResult> {
  const violations: BirhausTestResult['violations'] = []
  
  // Run cognitive load tests
  const cognitiveValidator = new MillersLawValidator(options.cognitive)
  const cognitiveViolations = cognitiveValidator.validateAll(container)
  violations.push(...cognitiveViolations)
  
  // Run accessibility tests
  const accessibilityValidator = new AccessibilityValidator(options.accessibility)
  const accessibilityViolations = await accessibilityValidator.validateAll(container)
  violations.push(...accessibilityViolations)
  
  // Run Spanish coverage tests
  const spanishValidator = new SpanishCoverageValidator(options.spanish)
  const spanishViolations = spanishValidator.validateAll(container)
  violations.push(...spanishViolations)
  
  // Run undo pattern tests
  const undoValidator = new UndoPatternValidator(options.undo)
  const undoViolations = await undoValidator.validateAll(container)
  violations.push(...undoViolations)
  
  // Calculate scores
  const cognitiveLoadScore = Math.max(0, 100 - (cognitiveViolations.length * 10))
  const accessibilityScore = Math.max(0, 100 - (accessibilityViolations.length * 15))
  const spanishCoverageScore = Math.max(0, 100 - (spanishViolations.length * 5))
  const undoPatternScore = Math.max(0, 100 - (undoViolations.length * 20))
  
  const overallScore = Math.round(
    (cognitiveLoadScore + accessibilityScore + spanishCoverageScore + undoPatternScore) / 4
  )
  
  // Determine if tests passed
  const criticalViolations = violations.filter(v => v.severity === 'critical' || v.severity === 'serious')
  const passed = criticalViolations.length === 0 && overallScore >= 80
  
  return {
    cognitiveLoadScore,
    accessibilityScore,
    spanishCoverageScore,
    undoPatternScore,
    overallScore,
    violations,
    passed
  }
}

/**
 * Assert comprehensive BIRHAUS compliance
 */
export async function expectBirhausCompliance(
  container?: HTMLElement,
  options?: BirhausTestOptions,
  minScore: number = 80
): Promise<void> {
  const result = await runBirhausTestSuite(container, options)
  
  if (!result.passed || result.overallScore < minScore) {
    const criticalViolations = result.violations.filter(v => 
      v.severity === 'critical' || v.severity === 'serious'
    )
    
    const errorMessages = criticalViolations
      .map(v => `${v.type}: ${v.message}`)
      .join('\n')
    
    throw new Error(
      `BIRHAUS compliance test failed (Score: ${result.overallScore}/${minScore}):\n\n` +
      `Cognitive Load: ${result.cognitiveLoadScore}/100\n` +
      `Accessibility: ${result.accessibilityScore}/100\n` +
      `Spanish Coverage: ${result.spanishCoverageScore}/100\n` +
      `Undo Patterns: ${result.undoPatternScore}/100\n\n` +
      `Critical violations:\n${errorMessages}\n\n` +
      `Total violations: ${result.violations.length} (${criticalViolations.length} critical)`
    )
  }
}

// Type declarations are provided by @types/jest-axe

// Vitest/Jest setup helpers
export function setupBirhausTests() {
  // Configure testing environment for BIRHAUS
  
  // Add custom matchers if needed
  if (typeof expect !== 'undefined' && expect) {
    expect.extend({
      toBirhausCompliant: async function(received: HTMLElement, options?: BirhausTestOptions) {
        try {
          await expectBirhausCompliance(received, options)
          return {
            message: () => 'Expected element to fail BIRHAUS compliance',
            pass: true
          }
        } catch (error) {
          return {
            message: () => `Expected element to be BIRHAUS compliant:\n${error}`,
            pass: false
          }
        }
      }
    })
  }
}

// Export all testing utilities for convenience
export * from './cognitive'
export * from './accessibility'
export * from './spanish'
export * from './undo'
export * from './visual'
export * from './performance'